import { useEffect, useState } from "react";
import { useBasket } from "@danishagro/shared/src/contexts/basket/basket.context";
import {
    DA_BasketData,
    DA_BasketOrderLine,
    DA_BasketPriceData,
} from "@danishagro/shared/src/interfaces/basket.interface";
import { ServicePoint } from "@interfaces/servicePoint.interface";

export const useCompletedOrder = (orderId: string, orderSecret: string) => {
    const [awaitingResponse, setAwaitingResponse] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [price, setPrice] = useState<DA_BasketPriceData>();
    const [customerDetails, setCustomerDetails] = useState<DA_BasketData["customerDetails"]>();
    const [shippingDetails, setShippingDetails] = useState<DA_BasketData["shippingDetails"]>();
    const [invoiceDetails, setInvoiceDetails] = useState<DA_BasketData["invoiceDetails"]>();
    const [completedDate, setCompletedDate] = useState<Date>();
    const [orderLines, setOrderLines] = useState<DA_BasketOrderLine[]>([]);
    const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
    const [deliveryMethod, setDeliveryMethod] = useState<string | null>(null);
    const [comment, setComment] = useState<string | null>(null);
    const [servicePoint, setServicePoint] = useState<ServicePoint | null>(null);

    const { getCompletedOrder } = useBasket();

    useEffect(() => {
        if (orderId && orderSecret) {
            setAwaitingResponse(true);
            setError(null); // Reset error state

            getCompletedOrder(orderId, orderSecret)
                .then((data) => {
                    // TODO: Figure out price format
                    setPrice(data.prices);
                    setCustomerDetails(data.customerDetails);
                    setShippingDetails(data.shippingDetails);
                    setInvoiceDetails(data.invoiceDetails);
                    setPaymentMethod(data.paymentMethod);
                    setDeliveryMethod(data.deliveryMethod);
                    setComment(data.comment);
                    setServicePoint(data.servicePoint || null);

                    // TODO: Figure out completedDate
                    setCompletedDate(data.completedDate);
                    setOrderLines(data.orderLines);
                })
                .catch((error) => {
                    console.error("Error fetching completed order:", error);

                    // Provide specific error messages based on error type
                    if (error.message?.includes("404") || error.status === 404) {
                        setError(
                            "Order not found. Please check your order details or contact support."
                        );
                    } else if (error.message?.includes("403") || error.status === 403) {
                        setError("You don't have permission to view this order.");
                    } else if (error.message?.includes("500") || error.status >= 500) {
                        setError("Server error occurred. Please try again later.");
                    } else if (error.message?.includes("network") || !navigator.onLine) {
                        setError(
                            "Network connection problem. Please check your internet connection and try again."
                        );
                    } else {
                        setError("Failed to load order details. Please try again later.");
                    }
                })
                .finally(() => setAwaitingResponse(false));
        }
    }, [getCompletedOrder, orderId, orderSecret]);

    return {
        awaitingResponse,
        error,
        price,
        customerDetails,
        shippingDetails,
        invoiceDetails,
        completedDate,
        orderLines,
        deliveryMethod,
        paymentMethod,
        comment,
        servicePoint,
    };
};
